
import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export const sanitizeHtml = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'sub', 'sup', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'span', 'a'],
    ALLOWED_ATTR: ['class', 'href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
};

/**
 * Sanitizes HTML produced by our TeX renderer. KaTeX emits `<span class="katex">`
 * wrappers plus a MathML branch for screen readers, none of which survive the
 * stricter `sanitizeHtml` allow-list above.
 */
export const sanitizeTexHtml = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'sub', 'sup', 'span', 'a', 'div',
      // KaTeX MathML output
      'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'ms', 'mtext',
      'msup', 'msub', 'msubsup', 'mfrac', 'msqrt', 'mroot', 'mspace',
      'mover', 'munder', 'munderover', 'mtable', 'mtr', 'mtd',
      'mpadded', 'mphantom', 'menclose', 'mstyle',
      'annotation', 'annotation-xml',
      // KaTeX SVG fragments (stretchy delimiters, rules)
      'svg', 'path', 'line', 'g',
    ],
    ALLOWED_ATTR: [
      'class', 'href', 'target', 'rel', 'style', 'aria-hidden', 'xmlns',
      'encoding', 'display', 'mathvariant', 'width', 'height', 'viewBox',
      'preserveAspectRatio', 'd', 'x1', 'x2', 'y1', 'y2', 'fill', 'stroke',
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
};

/**
 * Sanitizes plain text content
 */
export const sanitizeText = (content: string): string => {
  return DOMPurify.sanitize(content, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Validates and sanitizes user input
 */
export const validateAndSanitizeInput = (
  input: string,
  maxLength: number = 1000,
  allowHtml: boolean = false
): string => {
  if (!input) return '';
  
  // Trim whitespace
  let sanitized = input.trim();
  
  // Check length
  if (sanitized.length > maxLength) {
    throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
  }
  
  // Sanitize based on type
  return allowHtml ? sanitizeHtml(sanitized) : sanitizeText(sanitized);
};
